import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useSplitReveal } from '../lib/useSplitReveal';
import { ArrowRight, ImageUp } from 'lucide-react';
import StyleAnalyzerModal from './StyleAnalyzerModal';
import { PALETTE } from './backdrop/swatchField';

const DemoSection: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const reduce = useReducedMotion();
  const heading = useSplitReveal<HTMLHeadingElement>({ onScroll: true });
  const openModal = () => setIsModalOpen(true);

  return (
    <section id="demo-section" className="screen scroll-mt-16">
      <div className="shell">
      <motion.div
        initial={reduce ? false : { y: 24 }}
        whileInView={{ y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="panel-glass grid grid-cols-1 gap-6 px-5 py-7 sm:gap-12 sm:px-6 sm:py-14 md:px-12 md:py-20 lg:grid-cols-12 lg:gap-16 lg:px-16"
      >
        <div className="lg:col-span-6 lg:self-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-panel-accent">
            Color analysis
          </p>

          <h2
            ref={heading}
            className="display split-target mt-3 max-w-[12ch] text-[clamp(1.875rem,8vw,3.75rem)] font-medium sm:mt-5"
          >
            Find your signature palette.
          </h2>

          <p className="mt-4 max-w-[44ch] text-[14px] leading-relaxed text-panel-muted sm:mt-6 sm:text-[15px]">
            Our model reads whether your skin runs warm, cool or neutral, then maps that to the
            exact shades and contrast levels that hold up next to your face.
          </p>

          <button type="button" onClick={openModal} className="btn btn-on-panel group mt-6 w-full sm:mt-9 sm:w-auto">
            Analyze my palette
            <ArrowRight
              strokeWidth={1.5}
              className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
            />
          </button>

          <p className="mt-3 text-[12px] text-panel-muted sm:mt-4 sm:text-[13px]">
            No sign-up needed. Your photo stays on your device.
          </p>
        </div>

        <div className="lg:col-span-6">
          <button
            type="button"
            onClick={openModal}
            className="group flex h-[16svh] max-h-[150px] w-full flex-col items-center justify-center gap-2 border border-panel-line transition-colors duration-200 hover:border-panel-accent sm:aspect-[4/3] sm:h-auto sm:max-h-[34svh] sm:gap-3 lg:max-h-none"
          >
            <ImageUp
              strokeWidth={1.5}
              className="h-6 w-6 text-panel-muted transition-colors duration-200 group-hover:text-panel-accent sm:h-7 sm:w-7"
            />
            <span className="text-[13px] font-medium text-panel-muted transition-colors duration-200 group-hover:text-panel-accent">
              Upload a photo
            </span>
          </button>

          <div className="mt-3 grid grid-cols-6">
            {PALETTE.map((swatch) => (
              <div key={swatch.name}>
                <div className="h-8 sm:h-12" style={{ backgroundColor: swatch.hex }} />
                <p className="mt-1.5 text-[10px] font-medium uppercase tracking-[0.02em] text-panel-muted sm:mt-2 sm:text-[11px] sm:tracking-[0.08em]">
                  {swatch.name}
                </p>
              </div>
            ))}
          </div>

          <p className="mt-3 text-[12px] text-panel-muted sm:mt-4 sm:text-[13px]">
            An example set. Yours is generated from your own photo.
          </p>
        </div>
      </motion.div>

      <StyleAnalyzerModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      </div>
    </section>
  );
};

export default DemoSection;
